import { Component, forwardRef, Input, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, Validators } from '@angular/forms';
import { Article } from '../../../../core/models/article';
import { ArticleCamFile } from '../../../../core/models/article-cam-file';
import { ManufacturingOrder } from '../../../../core/models/manufacturing-order';
import { Model } from '../../../../core/models/model';
import { OperationProcess } from '../../../../core/models/operation-process';
import { OptionInterface } from '../../../../core/models/option.interface';
import { ArticleApiService } from '../../../../core/services/api/article-api.service';
import { find } from '../../../../core/utils/collection.utils';
import { buildOptions } from '../../../../core/utils/option.utils';
// tslint:disable-next-line: max-line-length
import { CamProgramArticleFormInterface } from '../../../../dashboard/cam/cam-program-article/cam-program-article-form.interface';
// tslint:disable-next-line: max-line-length
import { CamProgramArticleFormModelProgramItemInterface } from '../../../../dashboard/cam/cam-program-article/cam-program-article-form/cam-program-article-form-model-program-item.interface';
import { AbstractEditionModalForm } from '../edition-modal/abstract-edition-modal-form';

const ROUTE_SEPARATOR_STR = '/';
const PROGRAM_REFERENCE_ROUTE_INDEX = 3;

/**
 * Lightweight "add program to an OF" modal used from the BY_PART planner row.
 * The model is already fixed by the planner (selectedModelHash), so the user only
 * picks the operation (auto-selected when there is a single one) and types the
 * program reference for that model.
 *
 * IMPORTANT: even though the UI only edits one model, the save payload must rebuild
 * the FULL state of the CAM. The /articles-cams update endpoint deletes any model
 * path that is NOT resent (and throws if that path is planned), and it also wipes
 * CAM files/observations when their fields are absent. So getFormData() resends all
 * the existing models→programs for the operation plus the existing files/info,
 * swapping only the selected model's program reference.
 */
@Component({
  selector: 'app-scheduler-task-program-form',
  templateUrl: './scheduler-task-program-form.component.html',
  styleUrls: ['./scheduler-task-program-form.component.css'],
  providers:
    [{ provide: AbstractEditionModalForm, useExisting: forwardRef(() => SchedulerTaskProgramFormComponent) }]
})
export class SchedulerTaskProgramFormComponent
  extends AbstractEditionModalForm<any> implements OnInit {
  form: AbstractControl;

  @Input() manufacturingOrder: ManufacturingOrder;
  @Input() selectedModelHash: string;
  @Input() models: Model[] = [];

  loading = true;
  article: Article = null;
  operationProcesses: OperationProcess[] = [];
  operationProcessOptions: OptionInterface[] = [];
  selectedOperationProcess: OperationProcess = null;
  modelName = '';
  // Existing model→program associations for the selected operation, kept so the
  // save can resend the whole set (see class doc).
  private existingModelsPrograms: CamProgramArticleFormModelProgramItemInterface[] = [];

  constructor(
    private fb: FormBuilder,
    private articleApiService: ArticleApiService
  ) {
    super();
  }

  ngOnInit() {
    this.form = this.fb.group({
      operationProcess: [null, [Validators.required]],
      programReference: [null, [Validators.required]],
    });
    this.modelName = this.resolveModelName();
    this.loadArticleAndOperations();
  }

  updateForm(_model?: any, _params?: any) {
    // Data is loaded asynchronously in ngOnInit; nothing to reset here. The hook is
    // required by the EditionModal contract.
  }

  getFormData(): CamProgramArticleFormInterface {
    const programReference = this.normalizeProgramReference(this.form.get('programReference').value);
    return {
      camHash: this.article && this.article.cam ? this.article.cam.hash : null,
      articleHash: this.article ? this.article.hash : null,
      operationHash: this.form.get('operationProcess').value,
      modelsPrograms: this.buildModelsProgramsForSave(programReference),
      images: this.getExistingFiles(file => file.isImage),
      newImages: null,
      pdfs: this.getExistingFiles(file => file.isPDF),
      newPDFs: null,
      information: this.article && this.article.cam ? this.article.cam.observations : null,
    };
  }

  onChangeOperationProcess(option: any) {
    if (!option) {
      return;
    }
    this.selectedOperationProcess = find(
      (operation: OperationProcess) => operation.hash === option.value,
      this.operationProcesses
    );
    this.applySelectedOperation();
  }

  private loadArticleAndOperations() {
    const articleHash = this.resolveArticleHash();
    if (!articleHash) {
      this.loading = false;
      return;
    }
    this.articleApiService.getArticleByHash(articleHash).subscribe(
      (article: Article) => {
        this.article = article;
        this.articleApiService.getOperationProcessesByArticle(articleHash).subscribe(
          (operations: OperationProcess[]) => {
            this.operationProcesses = operations || [];
            this.operationProcessOptions = buildOptions(this.operationProcesses, 'hash', 'descriptionCode');
            this.autoSelectSingleOperation();
            this.loading = false;
          },
          () => this.loading = false
        );
      },
      () => this.loading = false
    );
  }

  private autoSelectSingleOperation() {
    if (this.operationProcesses.length === 1) {
      const operation = this.operationProcesses[0];
      this.form.get('operationProcess').setValue(operation.hash);
      this.selectedOperationProcess = operation;
      this.applySelectedOperation();
    }
  }

  private applySelectedOperation() {
    this.existingModelsPrograms = this.buildExistingModelsPrograms(this.selectedOperationProcess);
    const current = find(
      (modelProgram: CamProgramArticleFormModelProgramItemInterface) =>
        modelProgram.modelHash === this.selectedModelHash,
      this.existingModelsPrograms
    );
    this.form.get('programReference').setValue(current ? current.programReference : null);
  }

  private buildExistingModelsPrograms(operation: OperationProcess): CamProgramArticleFormModelProgramItemInterface[] {
    if (!operation || !operation.paths || !operation.paths.length) {
      return [];
    }
    const modelsPrograms: CamProgramArticleFormModelProgramItemInterface[] = [];
    const seenModels: string[] = [];
    operation.paths
      .slice()
      .sort((a, b) => a.model.hash.localeCompare(b.model.hash))
      .forEach(path => {
        if (seenModels.indexOf(path.model.hash) === -1) {
          seenModels.push(path.model.hash);
          const route = path.route ? path.route.split(ROUTE_SEPARATOR_STR) : null;
          modelsPrograms.push({
            modelHash: path.model.hash,
            modelName: path.model.nameReference,
            programReference: route ? route[PROGRAM_REFERENCE_ROUTE_INDEX] : null,
            edit: false
          });
        }
      });
    return modelsPrograms;
  }

  private buildModelsProgramsForSave(programReference: string): CamProgramArticleFormModelProgramItemInterface[] {
    const modelsPrograms = this.existingModelsPrograms.map(modelProgram => ({ ...modelProgram }));
    const existing = find(
      (modelProgram: CamProgramArticleFormModelProgramItemInterface) =>
        modelProgram.modelHash === this.selectedModelHash,
      modelsPrograms
    );
    if (existing) {
      existing.programReference = programReference;
    } else {
      modelsPrograms.push({
        modelHash: this.selectedModelHash,
        modelName: this.modelName,
        programReference: programReference,
        edit: false
      });
    }
    return modelsPrograms;
  }

  private getExistingFiles(predicate: (file: ArticleCamFile) => boolean) {
    if (!this.article || !this.article.cam || !this.article.cam.camFiles) {
      return [];
    }
    return this.article.cam.camFiles
      .filter(predicate)
      .map(file => ({
        hash: file.hash,
        filePath: file.filePath,
        fileName: file.fileName,
        fileUrl: (file.filePath || '') + (file.fileName || '')
      }));
  }

  private resolveArticleHash(): string {
    if (this.manufacturingOrder
      && this.manufacturingOrder.articleVersion
      && this.manufacturingOrder.articleVersion.article) {
      return this.manufacturingOrder.articleVersion.article.hash;
    }
    return this.manufacturingOrder ? this.manufacturingOrder.articleHash : null;
  }

  private resolveModelName(): string {
    const model = find((m: Model) => m.hash === this.selectedModelHash, this.models || []);
    return model ? model.nameReference : '';
  }

  private normalizeProgramReference(value: string): string {
    return value === null || value === undefined || value === '' ? null : value;
  }
}



// WEBPACK FOOTER //
// /var/www/borneo/prod/borneo_app/src/app/shared/components/modals/scheduler-task-program-form/scheduler-task-program-form.component.ts